/* montreveUS · global script: pub/sub, cart API, drawers, header, search, announcement, countdown, smooth loading. */
(() => {
  const wt = (window.wt = window.wt || {});
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Pub/sub ---------- */
  wt.emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));
  wt.on = (name, fn) => document.addEventListener(name, (e) => fn(e.detail));

  /* ---------- Analytics (consent-aware) ---------- */
  wt.track = (name, data = {}) => {
    if (!wt.analytics) return;
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    const allowed = privacy && typeof privacy.analyticsProcessingAllowed === 'function' ? privacy.analyticsProcessingAllowed() : false;
    if (!allowed) return;
    const analytics = window.Shopify && window.Shopify.analytics;
    if (analytics && typeof analytics.publish === 'function') analytics.publish(`wt_${name}`, data);
  };
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
  }

  /* ---------- Money ---------- */
  wt.formatMoney = (cents, format = wt.moneyFormat || '${{amount}}') => {
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const [i, d] = n.toFixed(decimals).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (d ? decimal + d : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2);
      }
    });
  };

  /* ---------- Cart API ---------- */
  const SECTIONS = ['wt-cart-drawer'];
  const post = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTIONS, sections_url: window.location.pathname })
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || wt.strings.cartError);
    return json;
  };

  wt.cart = {
    async add(items, meta = {}) {
      const json = await post(wt.routes.cartAdd, { items });
      await wt.cart.refresh(json.sections);
      wt.emit('wt:cart-update', { source: 'add', items, meta });
      if (!meta.silent) wt.cart.open();
      return json;
    },
    async change(line, quantity) {
      const json = await post(wt.routes.cartChange, { line, quantity });
      wt.cart.render(json.sections, json.item_count);
      wt.emit('wt:cart-update', { source: 'change', line, quantity, cart: json });
      return json;
    },
    async update(data) {
      const json = await post(wt.routes.cartUpdate, data);
      wt.cart.render(json.sections, json.item_count);
      wt.emit('wt:cart-update', { source: 'update', cart: json });
      return json;
    },
    async refresh(sections) {
      if (sections) return wt.cart.render(sections);
      const res = await fetch(`${window.location.pathname}?sections=${SECTIONS.join(',')}`);
      wt.cart.render(await res.json());
    },
    render(sections, count) {
      const html = sections && sections['wt-cart-drawer'];
      const drawer = $('wt-cart-drawer');
      if (html && drawer) {
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('wt-cart-drawer');
        if (fresh) {
          drawer.innerHTML = fresh.innerHTML;
          Object.assign(drawer.dataset, fresh.dataset);
          count = count ?? Number(fresh.dataset.itemCount);
        }
      }
      if (count !== undefined) wt.cart.setCount(count);
    },
    setCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = Number(count) === 0;
      });
    },
    open() {
      const drawer = $('wt-cart-drawer');
      if (drawer && wt.cartType === 'drawer') wt.drawer.open(drawer);
      else window.location.href = wt.routes.cart;
    }
  };

  /* ---------- Drawers (focus trap, Esc, overlay, scroll lock) ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';
  wt.drawer = {
    current: null,
    trigger: null,
    open(el, trigger) {
      if (this.current && this.current !== el) this.close(false);
      this.current = el;
      this.trigger = trigger || document.activeElement;
      el.hidden = false;
      $('[data-overlay]').hidden = false;
      document.documentElement.classList.add('wt-locked');
      $$(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const first = $('[data-drawer-close]', el) || $(FOCUSABLE, el);
      requestAnimationFrame(() => first && first.focus());
    },
    close(restore = true) {
      const el = this.current;
      if (!el) return;
      el.hidden = true;
      $('[data-overlay]').hidden = true;
      document.documentElement.classList.remove('wt-locked');
      $$(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.current = null;
      if (restore && this.trigger && this.trigger.focus) this.trigger.focus();
    }
  };

  document.addEventListener('keydown', (e) => {
    const el = wt.drawer.current;
    if (!el) return;
    if (e.key === 'Escape') { wt.drawer.close(); return; }
    if (e.key !== 'Tab') return;
    const items = $$(FOCUSABLE, el).filter((n) => n.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  });

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (t.closest('[data-overlay]')) { wt.drawer.close(); return; }
    if (t.closest('[data-drawer-close]')) { wt.drawer.close(); return; }
    const menuBtn = t.closest('[data-menu-open]');
    if (menuBtn) { wt.drawer.open($('#WtMenuDrawer'), menuBtn); return; }
    const cartBtn = t.closest('[data-cart-open]');
    if (cartBtn && wt.cartType === 'drawer' && $('wt-cart-drawer')) {
      e.preventDefault();
      wt.drawer.open($('wt-cart-drawer'), cartBtn);
    }
  });

  // The menu drawer is rendered inside the sticky header section, whose stacking context would
  // put it under the page overlay. Move it to <body> (again after Theme Editor re-renders).
  const hoistMenu = () => {
    const fresh = $$('#WtMenuDrawer').filter((d) => d.parentElement !== document.body);
    if (!fresh.length) return;
    $$('body > #WtMenuDrawer').forEach((old) => old.remove());
    document.body.appendChild(fresh[fresh.length - 1]);
  };
  hoistMenu();
  document.addEventListener('shopify:section:load', hoistMenu);

  /* ---------- Cart drawer element ---------- */
  class WtCartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-change]');
        if (!btn) return;
        e.preventDefault();
        const line = btn.closest('[data-line]');
        this.change(line, Number(btn.dataset.qtyChange));
      });
    }
    async change(lineEl, qty) {
      const index = Number(lineEl.dataset.line);
      lineEl.classList.add('is-busy');
      try {
        await wt.cart.change(index, Math.max(0, qty));
        const again = this.querySelector(`[data-line="${index}"] [data-qty-change]`) || this.querySelector('[data-drawer-close]');
        if (again) again.focus();
      } catch (err) {
        lineEl.classList.remove('is-busy');
        const body = this.querySelector('[data-cart-body]');
        const msg = document.createElement('p');
        msg.className = 'wt-cart-error';
        msg.setAttribute('role', 'alert');
        msg.textContent = err.message || wt.strings.cartError;
        body.prepend(msg);
      }
    }
  }
  customElements.define('wt-cart-drawer', WtCartDrawer);

  /* ---------- Header: dropdowns + compact sticky ---------- */
  const dropdowns = $$('details[data-dropdown]');
  const closeDropdowns = (except) => dropdowns.forEach((d) => { if (d !== except) d.open = false; });
  dropdowns.forEach((d) => {
    d.addEventListener('toggle', () => { if (d.open) closeDropdowns(d); });
    if (window.matchMedia('(hover: hover)').matches) {
      let timer;
      const item = d.closest('.wt-nav__item');
      item.addEventListener('mouseenter', () => { clearTimeout(timer); if (!d.open) { d.open = true; d.dataset.hoverAt = Date.now(); } });
      item.addEventListener('mouseleave', () => { timer = setTimeout(() => { d.open = false; }, 150); });
      // A click right after hover-opening would toggle it shut again; keep it open instead.
      $('summary', d).addEventListener('click', (e) => {
        if (d.open && Date.now() - Number(d.dataset.hoverAt || 0) < 1500) e.preventDefault();
        d.dataset.hoverAt = 0;
      });
    }
  });
  document.addEventListener('click', (e) => { if (!e.target.closest('details[data-dropdown]')) closeDropdowns(); });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = dropdowns.find((d) => d.open);
    if (open) { open.open = false; $('summary', open).focus(); }
  });

  // If a long menu doesn't fit beside the centred logo, fall back to the menu button.
  const headerEl = $('.wt-header');
  const navList = $('.wt-nav__list');
  if (headerEl && navList && 'ResizeObserver' in window) {
    const left = $('.wt-header__left', headerEl);
    new ResizeObserver(() => {
      headerEl.classList.remove('wt-header--overflow');
      if (window.innerWidth >= 990 && navList.scrollWidth > left.clientWidth) headerEl.classList.add('wt-header--overflow');
    }).observe(left);
  }

  const header = $('.wt-header[data-sticky="compact"]');
  if (header) {
    const wrap = header.closest('.shopify-section') || header;
    let ticking = false;
    // Two thresholds, so stopping near one scroll position can't flip the header on and off
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const on = wrap.classList.contains('is-compact');
        if (!on && y > 64) wrap.classList.add('is-compact');
        else if (on && y < 16) wrap.classList.remove('is-compact');
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Predictive search ---------- */
  const search = $('#WtSearch');
  if (search) {
    const input = $('#WtSearchInput', search);
    const results = $('[data-search-results]', search);
    const openBtn = $('[data-search-open]');
    let controller;
    let timer;
    const close = () => {
      search.hidden = true;
      openBtn && openBtn.setAttribute('aria-expanded', 'false');
    };
    openBtn && openBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const willOpen = search.hidden;
      closeDropdowns();
      if (!willOpen) { close(); return; }
      search.hidden = false;
      openBtn.setAttribute('aria-expanded', 'true');
      input.focus();
    });
    $('[data-search-close]', search).addEventListener('click', () => { close(); openBtn && openBtn.focus(); });
    search.addEventListener('keydown', (e) => { if (e.key === 'Escape') { close(); openBtn && openBtn.focus(); } });
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        const params = new URLSearchParams({
          q,
          section_id: 'wt-predictive-search',
          'resources[type]': 'product,collection,page',
          'resources[limit]': '6',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
        });
        try {
          const res = await fetch(`${wt.routes.predictiveSearch}?${params}`, { signal: controller.signal });
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const found = doc.querySelector('#WtPredictiveResults');
          results.innerHTML = found ? found.outerHTML : `<p class="wt-psearch__empty">${wt.strings.searchNoResults}</p>`;
        } catch (err) {
          if (err.name !== 'AbortError') results.innerHTML = '';
        }
      }, 250);
    });
  }

  /* ---------- Localization: submit on change ---------- */
  $$('[data-localization] select').forEach((s) => s.addEventListener('change', () => s.form.submit()));

  /* ---------- Announcement rotator ---------- */
  class WtAnnouncement extends HTMLElement {
    connectedCallback() {
      this.items = $$('.wt-announce__item', this);
      if (!this.items.length) return;
      this.index = 0;
      this.dataset.ready = '';
      this.show(0);
      const prev = $('[data-prev]', this);
      const next = $('[data-next]', this);
      prev && prev.addEventListener('click', () => this.step(-1));
      next && next.addEventListener('click', () => this.step(1));
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.items.length > 1 && !reduce) {
        const ms = Number(this.dataset.interval || 5) * 1000;
        const start = () => { this.timer = setInterval(() => this.step(1), ms); };
        const stop = () => clearInterval(this.timer);
        this.addEventListener('mouseenter', stop);
        this.addEventListener('mouseleave', start);
        this.addEventListener('focusin', stop);
        this.addEventListener('focusout', start);
        start();
      }
    }
    step(dir) { this.show((this.index + dir + this.items.length) % this.items.length); }
    show(i) {
      this.items.forEach((el, n) => el.classList.toggle('is-active', n === i));
      this.index = i;
    }
  }
  customElements.define('wt-announcement', WtAnnouncement);

  /* ---------- Countdown: fixed end, same for everyone, no storage ---------- */
  class WtCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end) * 1000;
      this.parts = { d: $('[data-d]', this), h: $('[data-h]', this), m: $('[data-m]', this), s: $('[data-s]', this) };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const left = this.end - Date.now();
      if (left <= 0) { clearInterval(this.timer); this.expire(); return; }
      const s = Math.floor(left / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      this.parts.d.textContent = Math.floor(s / 86400);
      this.parts.h.textContent = pad(Math.floor((s % 86400) / 3600));
      this.parts.m.textContent = pad(Math.floor((s % 3600) / 60));
      this.parts.s.textContent = pad(s % 60);
    }
    expire() {
      const campaign = this.closest('[data-campaign]');
      if (campaign) {
        campaign.hidden = true;
        const rotator = campaign.parentElement.querySelector('wt-announcement');
        if (rotator) rotator.hidden = false;
        else campaign.closest('.wt-announce').hidden = true;
      }
      const host = this.closest('[data-countdown-host]');
      if (host) host.hidden = true;
      this.hidden = true;
    }
  }
  customElements.define('wt-countdown', WtCountdown);

  /* ---------- Offer timer: 30 minutes per visit. Each visit (browser tab session) gets its own clock,
     kept across pages in sessionStorage, so every new visitor and every new visit starts at 30:00.
     Shown on the product page; hides at zero. ---------- */
  const OFFER_KEY = 'wt-offer-visit-ends';
  const offerEnd = (minutes) => {
    let end = 0;
    try { end = Number(sessionStorage.getItem(OFFER_KEY)) || 0; } catch (e) { /* storage blocked: timer runs for this page only */ }
    if (!end) {
      end = Date.now() + minutes * 60000;
      try { sessionStorage.setItem(OFFER_KEY, String(end)); } catch (e) { /* ignore */ }
    }
    return end;
  };
  class WtOfferTimer extends HTMLElement {
    connectedCallback() {
      this.end = offerEnd(Number(this.dataset.minutes || 30));
      this.time = $('[data-timer-time]', this);
      if (this.tick()) {
        this.hidden = false;
        this.timer = setInterval(() => this.tick(), 1000);
      }
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const left = Math.max(0, Math.round((this.end - Date.now()) / 1000));
      if (!left) { clearInterval(this.timer); this.hidden = true; return false; }
      this.time.textContent = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
      return true;
    }
  }
  customElements.define('wt-offer-timer', WtOfferTimer);

  /* ---------- Delivery estimate (store timezone, cut-off, ship days, holidays) ---------- */
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const cfg = wt.deliveryConfig;
  if (cfg && ((cfg.tmin !== null && cfg.tmax !== null) || cfg.emin !== null)) {
    const zonedNow = () => {
      const now = new Date();
      if (cfg.tz) {
        try {
          const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
            timeZone: cfg.tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23'
          }).formatToParts(now).map((p) => [p.type, p.value]));
          return { y: +parts.year, m: +parts.month, d: +parts.day, h: +parts.hour, min: +parts.minute };
        } catch (e) { /* invalid zone: fall back to the store offset */ }
      }
      const shifted = new Date(now.getTime() + cfg.offsetMinutes * 60000);
      return { y: shifted.getUTCFullYear(), m: shifted.getUTCMonth() + 1, d: shifted.getUTCDate(), h: shifted.getUTCHours(), min: shifted.getUTCMinutes() };
    };
    const iso = (date) => date.toISOString().slice(0, 10);
    const addDay = (date) => new Date(date.getTime() + 86400000);
    const isShipDay = (date) => cfg.weekdays.includes(DAYS[date.getUTCDay()]) && !cfg.holidays.includes(iso(date));
    const isTransitDay = (date) => date.getUTCDay() !== 0 && date.getUTCDay() !== 6;
    const addBusiness = (start, n, test) => {
      let d = start;
      let left = n;
      while (left > 0) { d = addDay(d); if (test(d)) left -= 1; }
      return d;
    };
    const fmt = new Intl.DateTimeFormat(cfg.locale || 'en', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

    wt.delivery = {
      estimate() {
        const n = zonedNow();
        let day = new Date(Date.UTC(n.y, n.m - 1, n.d));
        // "Expected delivery": a fixed number of calendar days after today, recounted every day
        if (cfg.emin !== null) {
          const plus = (k) => new Date(day.getTime() + k * 86400000);
          return { minutesLeft: null, shipBy: fmt.format(day), from: fmt.format(plus(cfg.emin)), to: fmt.format(plus(cfg.emax)) };
        }
        let minutesLeft = null;
        const cut = /^(\d{1,2}):(\d{2})$/.exec(cfg.cutoff || '');
        const nowMin = n.h * 60 + n.min;
        const cutMin = cut ? Number(cut[1]) * 60 + Number(cut[2]) : 24 * 60;
        if (isShipDay(day) && nowMin < cutMin) {
          if (cut) minutesLeft = cutMin - nowMin;
        } else {
          do { day = addDay(day); } while (!isShipDay(day));
        }
        const shipFirst = cfg.pmin ? addBusiness(day, cfg.pmin, isShipDay) : day;
        const shipLast = cfg.pmax ? addBusiness(day, cfg.pmax, isShipDay) : day;
        return {
          minutesLeft,
          shipBy: fmt.format(shipLast),
          from: fmt.format(addBusiness(shipFirst, cfg.tmin, isTransitDay)),
          to: fmt.format(addBusiness(shipLast, cfg.tmax, isTransitDay))
        };
      }
    };

    class WtDelivery extends HTMLElement {
      connectedCallback() { this.render(); this.timer = setInterval(() => this.render(), 60000); }
      disconnectedCallback() { clearInterval(this.timer); }
      render() {
        const e = wt.delivery.estimate();
        const range = e.from === e.to ? wt.strings.deliveryOn.replace('{date}', e.to) : wt.strings.deliveryRange.replace('{from}', e.from).replace('{to}', e.to);
        if (this.dataset.mode === 'short') { this.textContent = range; return; }
        let within = '';
        if (e.minutesLeft !== null) {
          const h = Math.floor(e.minutesLeft / 60);
          const m = e.minutesLeft % 60;
          within = wt.strings.orderWithin.replace('{time}', h ? `${h}h ${m}m` : `${m}m`);
        }
        this.innerHTML = within ? `<strong>${within}</strong> · ${range}` : range;
      }
    }
    customElements.define('wt-delivery', WtDelivery);
  }

  /* ---------- Tabs (ARIA, arrow keys) ---------- */
  class WtTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i, true));
        tab.addEventListener('keydown', (e) => {
          const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          const next = (i + dir + this.tabs.length) % this.tabs.length;
          this.select(next, true);
          this.tabs[next].focus();
          e.preventDefault();
        });
      });
      const start = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
      this.select(start, false);
    }
    select(index, user) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (user) wt.track('tab_opened', { tab: this.tabs[index].dataset.tabName });
    }
  }
  customElements.define('wt-tabs', WtTabs);

  /* ---------- Carousel (scroll-snap + buttons) ---------- */
  class WtCarousel extends HTMLElement {
    connectedCallback() {
      const track = $('[data-track]', this);
      const step = (dir) => track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: 'smooth' });
      const prev = $('[data-prev]', this);
      const next = $('[data-next]', this);
      prev && prev.addEventListener('click', () => step(-1));
      next && next.addEventListener('click', () => step(1));
    }
  }
  customElements.define('wt-carousel', WtCarousel);

  /* ---------- Product recommendations (lazy) ---------- */
  class WtRecommendations extends HTMLElement {
    connectedCallback() {
      new IntersectionObserver(async ([entry], obs) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        try {
          const res = await fetch(this.dataset.url);
          const fresh = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('wt-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
        } catch (e) { /* recommendations are optional */ }
      }, { rootMargin: '400px 0px' }).observe(this);
    }
  }
  customElements.define('wt-recommendations', WtRecommendations);

  /* ---------- Colour dropdown with pictures (cards + product page) ---------- */
  class WtColorSelect extends HTMLElement {
    connectedCallback() {
      this.btn = $('.wt-csel__btn', this);
      if (!this.btn || this.btn.disabled || this.ready) return;
      this.ready = true;
      this.btn.addEventListener('click', () => (this.list ? this.close() : this.open()));
      this.btn.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); this.open(); }
      });
    }
    disconnectedCallback() { this.close(false); }
    open() {
      if (WtColorSelect.current && WtColorSelect.current !== this) WtColorSelect.current.close(false);
      const scope = this.closest('[data-csel-scope]');
      const tpl = scope && $('template[data-csel-options]', scope);
      if (!tpl) return;
      const list = document.createElement('ul');
      list.className = 'wt-csel__list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', $('.wt-csel__label', this).textContent.trim());
      list.appendChild(tpl.content.cloneNode(true));
      $$('[role="option"]', list).forEach((o) => o.setAttribute('aria-selected', String(o.dataset.value === this.dataset.value)));
      document.body.appendChild(list);
      this.list = list;
      WtColorSelect.current = this;
      this.btn.setAttribute('aria-expanded', 'true');
      this.place();
      list.addEventListener('click', (e) => { const o = e.target.closest('[role="option"]'); if (o) this.choose(o); });
      list.addEventListener('keydown', (e) => this.onKey(e));
      this.onOutside = (e) => { if (!this.contains(e.target) && !list.contains(e.target)) this.close(false); };
      this.onMove = (e) => { if (e && e.target === list) return; requestAnimationFrame(() => this.list && this.place()); };
      document.addEventListener('pointerdown', this.onOutside, true);
      window.addEventListener('scroll', this.onMove, { passive: true, capture: true });
      window.addEventListener('resize', this.onMove);
      const sel = $('[aria-selected="true"]', list) || $('[role="option"]', list);
      if (sel) {
        sel.focus({ preventScroll: true });
        list.scrollTop = Math.max(0, sel.offsetTop - list.clientHeight / 2 + sel.offsetHeight / 2);
      }
    }
    place() {
      const r = this.btn.getBoundingClientRect();
      const list = this.list;
      if (r.bottom < 0 || r.top > window.innerHeight) { this.close(false); return; }
      const vw = document.documentElement.clientWidth;
      const width = Math.min(Math.max(r.width, 220), vw - 16);
      list.style.width = `${width}px`;
      list.style.left = `${Math.max(8, Math.min(r.left, vw - width - 8))}px`;
      const h = list.offsetHeight;
      const below = window.innerHeight - r.bottom;
      list.style.top = below < h + 12 && r.top > below ? `${Math.max(8, r.top - h - 6)}px` : `${r.bottom + 6}px`;
    }
    onKey(e) {
      const opts = $$('[role="option"]', this.list);
      const i = opts.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = opts[Math.max(0, Math.min(opts.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))];
        if (next) next.focus();
      } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        opts[e.key === 'Home' ? 0 : opts.length - 1].focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (i >= 0) this.choose(opts[i]);
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        if (e.key === 'Escape') e.preventDefault();
        this.close(e.key === 'Escape');
      }
    }
    choose(opt) {
      if (opt.getAttribute('aria-disabled') === 'true') return;
      this.setValue(opt.dataset.value);
      this.close(true);
      this.dispatchEvent(new CustomEvent('wt:color-select', {
        bubbles: true,
        detail: { slot: Number(this.dataset.slot), value: Number(opt.dataset.value), src: opt.dataset.src, name: opt.dataset.name }
      }));
    }
    // Shows a colour without announcing it (used when the page sets a pick itself)
    setValue(value) {
      const scope = this.closest('[data-csel-scope]');
      const tpl = scope && $('template[data-csel-options]', scope);
      const opt = tpl && tpl.content.querySelector(`[data-value="${value}"]`);
      if (!opt) return;
      this.dataset.value = String(value);
      $('[data-csel-name]', this).textContent = opt.dataset.name;
      const thumb = $('[data-csel-thumb]', this);
      if (thumb && opt.dataset.thumb) thumb.innerHTML = `<img src="${opt.dataset.thumb}" width="34" height="34" alt="">`;
      this.btn.setAttribute('aria-label', `${$('.wt-csel__label', this).textContent.trim()}: ${opt.dataset.name}`);
    }
    close(refocus = true) {
      if (!this.list) return;
      this.list.remove();
      this.list = null;
      if (WtColorSelect.current === this) WtColorSelect.current = null;
      this.btn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', this.onOutside, true);
      window.removeEventListener('scroll', this.onMove, { capture: true });
      window.removeEventListener('resize', this.onMove);
      if (refocus) this.btn.focus();
    }
  }
  customElements.define('wt-color-select', WtColorSelect);

  // Show a picture on the card without a blank flash: decode first, then swap
  const swapCardImage = (card, src) => {
    const img = $('.wt-card__img', card);
    if (!img || !src) return;
    card.dataset.swap = src;
    const swap = () => { if (card.dataset.swap !== src) return; img.removeAttribute('srcset'); img.src = src; };
    const next = new Image();
    next.src = src;
    (next.decode ? next.decode() : Promise.resolve()).then(swap, swap);
  };

  /* ---------- Product cards: each dropdown picks one watch of the pair and shows its colour ---------- */
  document.addEventListener('wt:color-select', (e) => {
    const card = e.target.closest('[data-card]');
    if (!card) return;
    swapCardImage(card, e.detail.src);
    const btn = $('[data-pair-add]', card);
    if (btn) btn.dataset[`v${e.detail.slot}`] = e.detail.value;
    const one = $('[data-quick-add]', card);
    if (one) one.dataset.quickAdd = e.detail.value;
  });

  /* Cards outside the offer keep their colour dots */
  document.addEventListener('click', (e) => {
    const dot = e.target.closest('[data-card-swatch]');
    if (!dot) return;
    const card = dot.closest('[data-card]');
    $$('[data-card-swatch]', card).forEach((d) => { d.classList.toggle('is-active', d === dot); d.setAttribute('aria-pressed', String(d === dot)); });
    swapCardImage(card, dot.dataset.src);
    const sel = $('[data-qa-select]', card);
    if (sel && dot.dataset.variant) sel.value = dot.dataset.variant;
  });
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-qa-select]');
    const card = sel && sel.closest('[data-card]');
    if (!card) return;
    const dot = $(`[data-card-swatch][data-variant="${sel.value}"]`, card);
    if (dot && !dot.classList.contains('is-active')) dot.click();
  });

  /* ---------- Buy 1, Get 1 Free pair on product cards: same watch, two colours, one click ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-pair-add]');
    if (!btn) return;
    const ids = [1, 2].map((n) => Number(btn.dataset[`v${n}`]));
    const items = ids[0] === ids[1] ? [{ id: ids[0], quantity: 2 }] : ids.map((id) => ({ id, quantity: 1 }));
    btn.classList.add('is-loading');
    try {
      await wt.cart.add(items);
      wt.track('pair_add', { variants: ids });
    } catch (err) {
      window.alert(err.message || wt.strings.cartError);
    } finally { btn.classList.remove('is-loading'); }
  });

  /* ---------- Quick add on product cards ---------- */
  document.addEventListener('click', async (e) => {
    const direct = e.target.closest('[data-quick-add]');
    const chosen = e.target.closest('[data-quick-add-select]');
    if (!direct && !chosen) return;
    const btn = direct || chosen;
    const id = direct ? direct.dataset.quickAdd : $('[data-qa-select]', btn.closest('.wt-qa')).value;
    btn.classList.add('is-loading');
    try {
      await wt.cart.add([{ id: Number(id), quantity: 1 }]);
      wt.track('quick_add', { variant: id });
      const details = btn.closest('details');
      if (details) details.open = false;
    } catch (err) {
      window.alert(err.message || wt.strings.cartError);
    } finally { btn.classList.remove('is-loading'); }
  });

  /* ---------- Cart page: same line controls, re-rendered by section ---------- */
  class WtCartPage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-qty-change]');
        if (!btn) return;
        e.preventDefault();
        const line = btn.closest('[data-line]');
        line.classList.add('is-busy');
        try {
          await wt.cart.change(Number(line.dataset.line), Math.max(0, Number(btn.dataset.qtyChange)));
          await this.refresh();
        } catch (err) {
          line.classList.remove('is-busy');
          window.alert(err.message || wt.strings.cartError);
        }
      });
      wt.on('wt:cart-update', (d) => { if (d.source === 'add') this.refresh(); });
    }
    async refresh() {
      const res = await fetch(`${window.location.pathname}?section_id=${this.dataset.section}`);
      const fresh = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('wt-cart-page');
      if (fresh) this.innerHTML = fresh.innerHTML;
    }
  }
  customElements.define('wt-cart-page', WtCartPage);

  /* ---------- Cart drawer extras: upsell, gift note, free-gift lines ---------- */
  document.addEventListener('click', async (e) => {
    const add = e.target.closest('[data-upsell-add]');
    if (!add) return;
    add.classList.add('is-loading');
    try {
      await wt.cart.add([{ id: Number(add.dataset.upsellAdd), quantity: 1 }]);
      wt.track('cart_upsell_added', { variant: add.dataset.upsellAdd });
    } finally { add.classList.remove('is-loading'); }
  });

  let noteTimer;
  document.addEventListener('input', (e) => {
    const field = e.target.closest('[data-cart-attr]');
    if (!field) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      const value = field.type === 'checkbox' ? (field.checked ? 'Yes' : '') : field.value;
      fetch(wt.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [field.dataset.cartAttr]: value } })
      });
    }, 500);
  });
  document.addEventListener('change', (e) => {
    const toggle = e.target.closest('[data-gift-toggle]');
    if (toggle) {
      $('[data-gift-fields]').hidden = !toggle.checked;
      if (toggle.checked) wt.track('gift_option_used', { source: 'cart' });
    }
  });

  // Free gifts are only kept when the automatic discount makes them free; a paid gift line is removed.
  let giftBusy = false;
  const rejectedGifts = new Set();
  const syncGift = async () => {
    const drawer = $('wt-cart-drawer');
    if (!drawer || giftBusy) return;
    const addId = Number(drawer.dataset.giftAdd || 0);
    const removeLine = Number(drawer.dataset.giftRemove || 0);
    if (!removeLine && (!addId || rejectedGifts.has(addId))) return;
    giftBusy = true;
    try {
      if (removeLine) await wt.cart.change(removeLine, 0);
      else {
        const res = await fetch(wt.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: addId, quantity: 1, properties: { _wt_gift: '1' } }] })
        });
        const json = await res.json();
        const line = json.items && json.items[0];
        if (line && line.final_line_price > 0) {
          console.warn('[wt] Gift line is not free: check the Buy X get Y discount in Admin. Removing it.');
          rejectedGifts.add(addId);
          const cart = await (await fetch(`${wt.routes.cart}.js`)).json();
          const index = cart.items.findIndex((i) => i.key === line.key) + 1;
          if (index) await wt.cart.change(index, 0);
        } else {
          await wt.cart.refresh();
        }
      }
    } catch (err) { /* leave the cart as it is */ }
    finally { giftBusy = false; }
  };
  wt.on('wt:cart-update', () => setTimeout(syncGift, 50));

  /* ---------- Shipping protection: a switch in the cart that adds or removes the protection line ---------- */
  const refreshCartPage = () => { const page = $('wt-cart-page'); if (page) page.refresh(); };
  document.addEventListener('change', async (e) => {
    const box = e.target.closest('[data-protection]');
    if (!box) return;
    box.disabled = true;
    try {
      if (box.checked) await wt.cart.add([{ id: Number(box.dataset.protection), quantity: 1 }], { silent: true, protection: true });
      else await wt.cart.change(Number(box.dataset.line), 0);
      wt.track('shipping_protection', { on: box.checked });
      refreshCartPage();
    } catch (err) {
      box.checked = !box.checked;
      box.disabled = false;
    }
  });
  // Protection on its own (every watch removed) goes too
  wt.on('wt:cart-update', () => setTimeout(async () => {
    const drawer = $('wt-cart-drawer');
    const line = Number(drawer && drawer.dataset.protectionAlone);
    if (!line) return;
    try { await wt.cart.change(line, 0); refreshCartPage(); } catch (err) { /* leave it */ }
  }, 80));

  /* ---------- 4 of the same watch: apply that model's 4-watch code (2 free + extra % off) ----------
     The free watch only works within one model (one automatic discount per watch), so 4 mixed watches get nothing extra.
     Codes are 4X-<handle without "montreve-">; any code the customer typed stays. */
  let codeBusy = false;
  const syncFourPack = async () => {
    if (codeBusy || !Array.isArray(wt.fourPack)) return;
    codeBusy = true;
    try {
      const cart = await (await fetch(`${wt.routes.cart}.js`)).json();
      const counts = {};
      cart.items.forEach((i) => { if (wt.fourPack.includes(i.handle)) counts[i.handle] = (counts[i.handle] || 0) + i.quantity; });
      const want = Object.keys(counts).filter((h) => counts[h] >= 4).map((h) => `4X-${h.replace(/^montreve-/, '').toUpperCase()}`);
      const current = (cart.discount_codes || []).map((d) => d.code.toUpperCase());
      const keep = current.filter((c) => !c.startsWith('4X-'));
      const next = [...keep, ...want];
      const same = next.length === current.length && next.every((c) => current.includes(c));
      if (!same) {
        await wt.cart.update({ discount: next.join(',') });
        refreshCartPage();
      }
    } catch (err) { /* leave the cart as it is */ }
    finally { codeBusy = false; }
  };
  wt.on('wt:cart-update', (d) => { if (d.source !== 'update') setTimeout(syncFourPack, 120); });

  /* ---------- Animations that start once their section is on screen ---------- */
  const players = $$('[data-play-on-view]');
  if (players.length) {
    if ('IntersectionObserver' in window) {
      const po = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        po.unobserve(entry.target);
        entry.target.classList.add('is-play');
      }), { threshold: 0.35 });
      players.forEach((el) => po.observe(el));
    } else players.forEach((el) => el.classList.add('is-play'));
  }

  /* ---------- Smooth loading: fade pictures in, reveal sections as they scroll into view ---------- */
  document.documentElement.classList.add('wt-fade');
  $$('img[loading="lazy"]').forEach((img) => { if (img.complete) img.classList.add('is-ready'); });
  document.addEventListener('load', (e) => {
    const img = e.target;
    if (img.tagName === 'IMG' && img.loading === 'lazy' && !img.classList.contains('is-ready')) img.classList.add('is-loaded');
  }, true);
  document.addEventListener('error', (e) => { if (e.target.tagName === 'IMG') e.target.classList.add('is-ready'); }, true);

  if (!matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
    // Sections holding fixed panels are left alone: a transform would pin those panels to the section
    const skip = '.wt-sticky, wt-sticky-atc, .wt-facets__panel, [data-no-reveal]';
    const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      entry.target.classList.add('is-in');
      setTimeout(() => entry.target.classList.remove('wt-reveal', 'is-in'), 1400);
    }), { rootMargin: '0px 0px -40px 0px' });
    $$('main > .shopify-section').forEach((section, i) => {
      if (i === 0 || section.querySelector(skip) || section.getBoundingClientRect().top < window.innerHeight) return;
      section.classList.add('wt-reveal');
      $$('.wt-grid > li', section).forEach((li, n) => li.style.setProperty('--i', n % 4));
      io.observe(section);
    });
  }
})();
